import React from 'react';
import {
  AppShell,
  UnisporkalRoutes,
  UnisporkalRoute,
  type AppRoutesOptions,
  withRouting,
} from '@unisporkal/federation';
import { lazyComponent, lazyProps } from '@unisporkal/react-lazy-component';
import { TrackingData } from '@unisporkal/federation';
import { LogEvent } from '@unisporkal/instrumentation';
import provideResolvedSearch from './middleware/provideResolvedSearch';
import { MOUNTED_ROUTES } from './mountedRoutes';

const GalleryWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="content_wrapper">
    <div className="content_wrapper__mask content_wrapper__mask--hidden" />
    {children}
  </div>
);

const Translations = lazyProps(
  (props) => import(`locales/${props.siteName}/${props.locale}.yml`)
);

const SearchPage = lazyComponent(() => import('../pages/Search')) as (props: {
  siteName?: string;
}) => React.ReactNode;

// Routes that (a) flow through the Rails SearchControllerBase (SearchController,
// GatewayController, EditorialLandingController), rendering the search results
// page (SRP), AND (b) are actually forwarded to this app by the haproxy front
// door (app_search) — haproxy is a hard gate in front of this app, so a Rails
// route that exists but isn't in haproxy's allowlist is unreachable in
// production (confirmed: "wektory" 404s despite being a live Rails route).
// Source of truth is the intersection of config/routes.rb (cross-checked
// against `bundle exec rails routes -c gateway|search|editorial_landing` to
// capture every locale) and the haproxy allowlist for app_search. Grouped by
// the concrete controller that handles each route. Path patterns mirror the
// live Rails routes (including :params) so they match the same URLs. The `*`
// catch-all below remains the fallback, so this list is declarative
// scaffolding (no runtime behavior change). Slugs are matched
// case-insensitively by React Router (unlike Rails), so Rails' capitalized
// duplicates (e.g. "Photos" alongside "photos") are omitted here as
// redundant.
//
// Excluded on purpose: base-controller actions that redirect rather than render
// the SRP (search#facets, search#more_from_this_artist), and front-door slugs that
// route to redirect controllers, NOT the base controller — créatif / クリエイティブ /
// エディトリアル / haber-amaçlı (search_redirects#category_deprecated), and
// galleries/search, galleries/keywords, bare `search` (301 redirects).

export type AppRoutesFunctionType = (
  options: AppRoutesOptions
) => React.ReactElement;

export type SiteComponentDefinition = {
  Component: React.ComponentType<{ store: unknown }>;
  configureStore: (...args: unknown[]) => unknown;
};

export type SiteComponentType = (
  site: string,
  componentName: string
) => SiteComponentDefinition;

export type AppRoutesAdapterFunctionType = (
  siteComponent: SiteComponentType
) => AppRoutesFunctionType;

export const appRoutesV2: AppRoutesFunctionType = () => (
  <UnisporkalRoutes>
    <UnisporkalRoute
      path="search/2/image"
      pageName="search-image"
      SiteComponent={SearchPage}
    />
  </UnisporkalRoutes>
);

export const getRoutesV2 = withRouting('search', appRoutesV2, { Translations });

export const getRoutes = (
  siteName: string,
  componentName: string,
  siteComponent: SiteComponentType,
  payload: Record<string, unknown>,
  logEvent?: LogEvent
) => {
  const { Component, configureStore } = siteComponent(
    siteName as string,
    componentName
  );

  const store = configureStore(payload);

  // The search the rendered page ran, as the server resolved it: `Query.create`
  // puts the url, the stored preferences and the defaults through
  // `SearchParams#build` and serializes the answer here. Handed to the entry
  // middleware only to be compared against — see `searchResolutionDrift`.
  const searchedParameters = (
    payload.search as
      | { query?: { params?: Record<string, unknown> } }
      | undefined
  )?.query?.params;

  const railsPageName = (
    payload.search as { trackingData?: { page_name?: string } } | undefined
  )?.trackingData?.page_name;

  const SearchRouteTracking = ({ pageName }: { pageName: string }) => (
    <TrackingData
      data={railsPageName ? { page_name: railsPageName } : {}}
      pageName={pageName}
    />
  );
  SearchRouteTracking.displayName = 'SearchRouteTracking';

  const SearchComponent = () => (
    <Component
      store={store}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...payload}
    />
  );

  const Routes = () => (
    <UnisporkalRoutes>
      <AppShell
        documentMiddleware={[
          provideResolvedSearch(logEvent, searchedParameters),
        ]}
        globalNavType="full"
        RouteWrapper={GalleryWrapper}
        showSearchBar={false}
        showCNS
        disableCookieConsent
      >
        {MOUNTED_ROUTES.map(({ path, pageName, staticParams }) => (
          <UnisporkalRoute
            key={path}
            path={path}
            staticParams={staticParams}
            pageName={pageName}
            SiteComponent={SearchComponent}
            trackingData={SearchRouteTracking}
          />
        ))}
        <UnisporkalRoute
          path="*"
          pageName="search"
          loader={({ request }: { request: Request }) => {
            logEvent?.handledError(
              'Unmatched search route fell through to catch-all',
              new Error(`No explicit route matched: ${request.url}`)
            );
            return {};
          }}
          SiteComponent={SearchComponent}
          trackingData={SearchRouteTracking}
        />
      </AppShell>
    </UnisporkalRoutes>
  );

  return withRouting('search', Routes, { Translations });
};
